import { Action, PriceList } from './action';
import { CustomerType } from './client-directory';
import { CustomerSetting } from './customer-setting';
import { WorkFlow } from './workflow-step';
import { DeviationType } from './deviation';
import { OrderTag } from './tags';
import { Address, OrganizationContact } from './contact';
import { Invoicing } from './common';
import { LocaleFormat } from './locale-format';
import { WaybillPackageSize } from './transport';
import { User } from './user';
import { MaterialMacro } from './material-macro';
import { ProfileText, SupplierProfile } from './organization-profile';

export type OrgLookup = {
    organizationDbId?: string;
    existsInDb: boolean;
    hitHorizonsId: string;
    name: string;
    type: string;
    isAlreadyConnected: boolean;
    organizationNumber: string;
    vatNumber?: string;
    address: Address;
    site?: string;
    isWhiteLabeled: boolean;
};

export type PaymentDetails = {
    postgiro?: string;
    bankgiro?: string;
    bicSwift?: string;
    iban?: string;
    vatNo?: string;
    paymentBoxAccount?: string;
    /** DD-3700: Norwegian labs: the domestic account number (kontonummer). */
    accountNumber?: string;
    /** DD-3700: Norwegian labs: the KID agreement with the bank. */
    kid?: KidSettings;
    /** DD-3700: Danish labs: the bank registration number (reg.nr.). */
    registrationNumber?: string;
    /** DD-3700: Danish labs: the FI agreement (indbetalingskort) with the bank. */
    fi?: FiSettings;
    /** DD-3700: Finnish labs: NATIONAL (viitenumero) or RF. */
    referenceFormat?: string;
};

export type FiSettings = {
    enabled?: boolean;
    cardType?: string;
    creditorNumber?: string;
};

export type KidSettings = {
    enabled?: boolean;
    length?: number;
    checkDigitMethod?: string;
};

export enum InvoicePaymentMethod {
    COLLECTIVE = 'COLLECTIVE',
    COLLECTIVE_DENTIST = 'COLLECTIVE_DENTIST',
    PER_ORDER = 'PER_ORDER',
}

enum InvoiceNumberType {
    MANUAL = 'MANUAL',
    INCREMENTAL = 'INCREMENTAL',
}

export interface InvoiceConfiguration {
    invoiceNumberType?: InvoiceNumberType;
    currentStartingNumber?: number;
    monthlyInvoiceDay?: number;
    isShared?: boolean;
}

export interface InvoiceClinic {
    clinicId?: string;
    paymentTerms?: number;
    method?: InvoicePaymentMethod;
    invoice?: boolean;
}

interface SyncUserProperties {
    name: {
        firstName: string;
        lastName: string;
    };
    email: string;
}

interface SyncUser {
    id: string;
    properties: SyncUserProperties;
}

interface OrgUser {
    user: string;
    role: string;
}

interface Statistics {
    clinicId: string;
    numOrders: number;
    ratings: any;
}

interface OrgExternalInfo {
    externalId1?: string;
    externalId2?: string;
}

export type ExtendedMaterialGroup = MaterialGroup & {
    materialPriceDifferenceForGroup: MaterialPriceDifference[];
};

export type GroupMaterials = {
    _id?: string;
    material: string;
};

export interface MaterialGroup {
    _id: string;
    name: string;
    materials: GroupMaterials[];
    actionSet: string;
}

export interface SubCategory {
    _id?: string;
    name: string;
    selected?: boolean;
    colorCode: string;
    textColorCode: string;
    parentCategoryId: string;
}

export interface Category {
    _id?: string;
    name: string;
    colorCode: string;
    textColorCode: string;
    instructions: string;
    subCategories: SubCategory[];
    actionSet: string;
}

export interface MaterialPriceDifference {
    materialGroupId?: string;
    materialGroupName?: string;
    materials: {
        material: string;
        materialName: string;
        priceDiff: number;
        isAvailable?: boolean;
    }[];
}

export interface ConnectedLab {
    lab: Organization;
    _id?: string;
    isDisabled: boolean;
    isRequireException: boolean;
    currency?: string;
    priceList: PriceList[];
    materialPriceDifference: MaterialPriceDifference[];
    connectedDate: number;
}

export type Material = {
    _id?: string;
    __typename?: string;
    material?: string;
    code?: string;
    description: string;
    price?: number;
    vat?: number;
    createdOn?: string;
    batchNumber?: string;
    inactive?: boolean;
    inactiveDate?: number;
    autoReduction?: boolean;
    quantity?: number;
    discountedPrice?: number;
    inputValue?: string;
};

export type ActionNew = {
    _id?: string;
    __typename?: string;
    categoryId?: string;
    code?: string;
    description: string;
    subCategory?: string;
    name?: string;
    number?: string;
    price?: number;
    vat?: number;
    createdOn?: string;
    inactive?: boolean;
    inactiveDate?: number;
    discountedPrice?: number;
    quantity?: number;
    action?: string;
    actionObj?: any;
    fullActionName?: string;
    inputValue?: string;
    isMaterialMandatory?: boolean;
    actionMaterials?: ActionMaterials[];
};

export interface CategoryNew {
    _id?: string;
    name?: string;
}
export interface Hours {
    day: string;
    time: {
        from: number;
        to: number;
    };
    active: boolean;
    dayNum?: number;
}

export interface AssociatedActionList {
    _id: string;
    actionListId: string;
    generalDiscount: string;
    actions: ActionNew[];
    lab: string;
    categoryId: string;
    categories: CategoryNew[];
}

export type OrgGeneralNotifications = {
    newOrder: boolean;
    newMessage: boolean;
    orderModified: boolean;
    orderResend: boolean;
    orderCancelled: boolean;
    orderRated: boolean;
    orderReceivedByLab?: boolean;
    orderSentByLab?: boolean;
    orderDeclined?: boolean;
    // DD-3492: orders this lab placed with a partner lab.
    placedOrderNewMessage?: boolean;
    // DD-3633: the "System notifications" section.
    priceRequests?: boolean;
    invoiceBatches?: boolean;
};

export type OrgEmailNotifications = {
    type: string;
    newOrder: boolean;
    newMessage: boolean;
    orderModified: boolean;
    orderResend: boolean;
    orderCancelled: boolean;
    orderRated: boolean;
    orderReceivedByLab?: boolean;
    orderSentByLab?: boolean;
    orderDeclined?: boolean;
    // DD-3492: orders this lab placed with a partner lab.
    placedOrderNewMessage?: boolean;
    // DD-3633: the "System notifications" section.
    priceRequests?: boolean;
    invoiceBatches?: boolean;
    otherEmail: string | string[];
};

export type OrgExternalGeneralNotifications = {
    newInternalMessage: boolean;
    internalOrderModified: boolean;
    orderModified: boolean;
    orderDeclined: boolean;
    assignExtTech: boolean;
};

export type OrgExternalEmailNotifications = {
    newInternalMessage: boolean;
    internalOrderModified: boolean;
    orderModified: boolean;
    orderDeclined: boolean;
    assignExtTech: boolean;
};

export type OrgLocalDentistNotifications = {
    orderRecievedByLab: boolean;
    orderSentByLab: boolean;
};

export type VismaCodingIds = {
    unitId: string;
    noVatCodingId: string;
    vatCodingId: string;
};

export type TransportPartnerService = {
    id: string;
    description: string;
    paymentMethodType?: string;
    addons: string[];
};
export type TransportPartner = {
    id: string;
    description: string;
    services: TransportPartnerService[];
};

export type DenthubTransport = {
    isEnabled: boolean;
    externalId: string;
};

export type NShiftTransport = {
    isEnabled: boolean;
    partners: TransportPartner[];
    waybillPrintFormat: string;
    waybillPackageSizes: WaybillPackageSize[];
};

export type TransportSettings = {
    denthubTransport: DenthubTransport;
    nShiftTransport: NShiftTransport;
};

export type ClinicNote = {
    lab: string;
    note: string;
};

export type Period = {
    monthly: boolean;
    semiWeekly: boolean;
    weekly: boolean;
};

export interface Organization {
    _id: string;
    __typename?: string;
    name: string;
    officialName: string;
    customerType?: CustomerType;
    organizationNumber: string;
    ptjCustomerNumber?: string;
    materialListName: string;
    actionListName?: string;
    orderVersion?: string;
    transportSettings?: TransportSettings;
    gln: string;
    type: string;
    local?: boolean;
    isWhiteLabeled?: boolean;
    isMain?: boolean;
    localLabOwner?: string;
    isNotPickUpAndDeliveryPlace: boolean;
    isPickUpClinic: boolean;
    relatedClinics?: any;
    deliveryInstructions?: string;
    identity: string;
    contact: OrganizationContact;
    externalInfo: OrgExternalInfo;
    users: OrgUser[];
    parentClinicId: string;
    parentClinic?: Organization;
    active: boolean;
    shortDescription: string;
    longDescription: string;
    // DD-3662: the clinic-facing texts per language, and the supplier profile.
    profileTexts?: ProfileText[] | null;
    supplierProfile?: SupplierProfile | null;
    defaultJobs: {
        name: string;
        deliveryDays: number;
        active: boolean;
    }[];
    currentJobs: {
        name: string;
        deliveryDays: number;
        active: boolean;
    }[];
    logoImage: string;
    staffImage: string;
    documents: {
        _id: string;
        document: any;
    }[];
    selectedDocs: {
        document: any;
    }[];
    signedDocuments: {
        document: any;
    }[];
    defaultHours: Hours[];
    openingHours: Hours[];
    isRemoved: boolean;
    holidayDays: number[];
    activeDates: any;
    unavailableDates: any;
    unavailableLabDates: number[];
    connectedLabs: ConnectedLab[];
    export: {
        enable: boolean;
        additionalDeliveryDays: number;
    };
    publishLab: boolean;
    emailNotifications: {
        type: string;
        otherEmail: string;
        newOrder: boolean;
        newMessage: boolean;
        orderModified: boolean;
        orderRated: boolean;
        orderCancelled: boolean;
        orderResend: boolean;
    };
    generalNotifications: OrgGeneralNotifications;
    externalGeneralNotifications: OrgExternalGeneralNotifications;
    externalEmailNotifications: OrgExternalEmailNotifications;
    notificationListDefaults?: {
        showOnlyUnread?: boolean;
        showNotDone?: boolean;
    };
    created: {
        on: number;
        by: string;
    };
    updated: {
        on: number;
        by: string;
    };
    settings: any;
    syncUsers: SyncUser[];
    materialGroups: MaterialGroup[];
    categories: Category[];
    actions: Action[];
    customerSettings: CustomerSetting[];
    vismaCodingIds?: VismaCodingIds;
    totalRatings: number;
    satisfiedRatings: number;
    statistics: Statistics[];
    invoicing: Invoicing;
    clinicInvoicing: {
        [key: string]: InvoiceClinic;
    };
    crediFlow: {
        partyID: number;
    };
    isvalidOpeningHoursTime: boolean;
    companyEmail: string;
    selected: boolean;
    defaultActionListId: string;
    associatedActionList: AssociatedActionList[];
    labAssociatedActionListForClient: AssociatedActionList;
    isTransportEnable?: boolean;
    isTransportEnabled?: boolean; // FIXME: if true => transport icon is shown on labClient (clinic) in client directory list (naming???)
    isApiEnabled?: boolean;
    hexCode?: string;
    textCode?: string;
    clinicName?: string;
    logoSignedUrl: string;
    staffSignedUrl: string;
    workFlow: WorkFlow;
    defaultMaterialPriceListId: string;
    defaultActionPriceListId: string;
    deviationTypes: DeviationType[];
    orderTags: OrderTag[];
    hitHorizonsId?: string;
    invoiceType?: string;
    clinicInternalNotes?: ClinicNote[];
    invoicePeriod?: string;
    activeActionSet?: string;
    actionSets?: OrgActionSet[];
    sourceCurrency?: string;
    departments?: Department[];
    localeFormat?: LocaleFormat;
}

export type OrgActionSet = {
    _id: string;
    title: string;
    currency?: string;
};

export type ActionMaterials = {
    _id: string;
    priceDiff: number;
    material: string;
    materialName: string;
};

export type AssociatedAction = {
    __typename?: string;
    _id?: string;
    subCategory?: string;
    name?: string;
    number?: string;
    code?: string;
    internalCode?: string;
    description?: string;
    inactive?: boolean;
    inputValue?: string;
    price?: number;
    tiotandvardPrice?: number;
    vat?: number;
    isMaterialMandatory?: boolean;
    actionMaterials?: ActionMaterials[];
};

export type TenderedProductionTemplateRef = {
    digitalTemplateId?: string | null;
    physicalTemplateId?: string | null;
};

export type TenderedProductionTemplate = {
    productionPlanningTemplate?: TenderedProductionTemplateRef;
    productionValueTemplate?: TenderedProductionTemplateRef;
};

export type TenderedActionProductionTemplate = {
    actionId: string;
    internalCode?: string;
    productionPlanningTemplate?: TenderedProductionTemplateRef;
    productionValueTemplate?: TenderedProductionTemplateRef;
    productionDeductionValue?: number;
    materialMacros?: string[];
};

type Template = {
    _id: string;
    name: string;
};

export type ActionProductionTemplate = {
    planningDigitalTemplateId?: Template;
    planningPhysicalTemplateId?: Template;
    valueDigitalTemplateId?: Template;
    valuePhysicalTemplateId?: Template;
    productionDeductionValue?: number;
};

export type ActionPrice = {
    _id: string;
    action: string;
    name: string;
    mainCategory: string;
    subCategory: string[];
    number: string;
    internalCode?: string;
    price: number;
    tiotandvardPrice?: number;
    vat: number;
    deliveryTime: number;
    materialGroup: string;
    materialMacros?: MaterialMacro[];
    enabled: boolean;
    productionTemplate?: ActionProductionTemplate | null;
};

export type CategorizedActions = {
    __typename?: string;
    categoryId: string;
    categoryName: string;
    subCategoryId: string;
    subCategoryName: string;
    currency?: string;
    actions: AssociatedAction[];
};

export type ActionsAndMaterialList = {
    materials: Material[];
    categorizedActions: CategorizedActions[];
    actionNew: ActionNew[];
    categoryNew: CategoryNew[];
};

export type LocalClinic = {
    _id?: string;
    name: string;
    organizationNumber?: string;
    ptjCustomerNumber?: string;
    gln?: string;
    contact: OrganizationContact;
    invoice: Address;
    externalInfo?: OrgExternalInfo;
};

export type LabSettingsInclude = {
    [key: string]: {
        opening: { hour: number; minute: number };
        closing: { hour: number; minute: number };
        active: boolean;
        dayNum: number;
    };
};

export type LabSettings = {
    [key: string]: {
        opening: { hour: number; minute: number };
        closing: { hour: number; minute: number };
        active: boolean;
    };
};

export type CurrentJobs = { name: string; active: boolean; deliveryDays: number };

export type Payments = {
    postgiro: string | undefined;
    bankgiro: string | undefined;
    bicSwift: string | undefined;
    iban: string | undefined;
};

export type SalesforceUser = {
    id: string;
    name: string;
    email: string;
};

// shared calendar component types
export type CalendarDay = {
    day: number;
    color?: string;
    // Full CSS background, for a day that needs more than a flat colour - a day that
    // is both a holiday and unavailable is split half and half. Takes precedence
    // over `color` when set.
    background?: string;
    textColor?: string;
    disabled?: boolean;
    data?: any;
};

export type CalendarMonth = {
    month: string;
    days: CalendarDay[];
    subtitle?: string;
    totalDays?: number;
};

export type CalendarLegend = {
    color: string;
    label: string;
};

export type Gridprops = {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
};

export type DepartmentWorkflowStepInput = {
    stepId: string;
    stepOrder: number;
};

export type DepartmentInput = {
    _id?: string;
    name: string;
    workflowSteps: DepartmentWorkflowStepInput[];
    technicians: string[];
    productionHourLimit?: number;
    capacity?: number;
};

export enum BundleDocumentType {
    PATIENT_SPECIFICATION = 'PATIENT_SPECIFICATION',
    ORDER_SUMMARY = 'ORDER_SUMMARY',
    ORDER_FORM = 'ORDER_FORM',
    WORK_SHEET_V2 = 'WORK_SHEET_V2',
    WORK_SHEET_SUBCONTRACTOR = 'WORK_SHEET_SUBCONTRACTOR',
    PATIENT_LABEL = 'PATIENT_LABEL',
    DESTINATION_ADDRESS_LABEL = 'DESTINATION_ADDRESS_LABEL',
    ORDER_RECEIPT = 'ORDER_RECEIPT',
    // Pre-generated documents fetched from S3 (signed URL), not generated on the fly.
    DELIVERY_NOTE = 'DELIVERY_NOTE',
    INVOICE = 'INVOICE',
    DEVIATION_REPORT = 'DEVIATION_REPORT',
}

export type DocumentBundleItem = {
    documentType: BundleDocumentType;
    copies: number;
};

export type DocumentBundle = {
    _id?: string;
    title: string;
    documents: DocumentBundleItem[];
};

export type DocumentBundleInput = {
    _id?: string;
    title: string;
    documents: DocumentBundleItem[];
};

export type Step = {
    _id: string;
    name: string;
};

export type PlanningWorkflowStep = {
    _id: string;
    name: string;
    stepOrder?: number | null;
    departmentId: string;
    machineId?: string;
    estimatedProductionHours?: number;
    estimatedMachineHours?: number;
    allowOvernight?: boolean;
    startNextDay?: boolean;
    affectedByQuantityModifier?: number;
    stepId?: string;
};

export type ProductionPlanningTemplate = {
    _id?: string;
    name: string;
    workflowSteps: PlanningWorkflowStep[];
};

export type PlanningWorkflowStepInput = {
    stepId: string;
    stepOrder: number | null;
    departmentId: string;
    machineId?: string;
    estimatedProductionHours: number;
    estimatedMachineHours?: number;
    allowOvernight?: boolean;
    startNextDay?: boolean;
    affectedByQuantityModifier?: number;
};

export type ProductionPlanningTemplateInput = {
    template: {
        templateId?: string;
        name: string;
        workflowSteps: PlanningWorkflowStepInput[];
    };
};

export enum ListViewType {
    LAB_ORDER_LIST = 'LAB_ORDER_LIST',
}

export type ListViewColumn = {
    key: string;
    visible: boolean;
};

export type ListViewProfile = {
    _id: string;
    name: string;
    listType: ListViewType;
    columns: ListViewColumn[];
    showIconArea?: boolean;
    defaultSortField?: string;
    defaultSortDirection?: 'asc' | 'desc';
};

export type OrderListProfileDefaults = {
    mainProfileId: string | null;
    workflowProfileId: string | null;
};

export type OrderListScope = 'MAIN' | 'WORKFLOW';

export type ListViewProfileInput = {
    profileId?: string;
    listType: ListViewType;
    name: string;
    columns: ListViewColumn[];
    showIconArea?: boolean;
    defaultSortField?: string;
    defaultSortDirection?: 'asc' | 'desc';
};

export type ProductionValueStep = {
    _id: string;
    name: string;
    departmentId: string;
    /** DD-3545: workflow steps whose technician fills this step in the production value report */
    workflowStepIds?: string[];
};

export type ProductionValueTemplateStep = {
    _id: string;
    stepId: string;
    stepOrder?: number | null;
    valuePercent: number | '';
    name?: string;
};

export type ProductionValueTemplate = {
    _id?: string;
    name: string;
    steps: ProductionValueTemplateStep[];
};

export type ProductionValueTemplateStepInput = {
    stepId: string;
    stepOrder: number | null;
    valuePercent: number;
};

export type ProductionValueTemplateInput = {
    template: {
        templateId?: string;
        name: string;
        steps: ProductionValueTemplateStepInput[];
    };
};

export type ProductionValueStepInput = {
    step: {
        name: string;
        departmentId: string;
    };
};

export type UpdateProductionValueStepInput = {
    stepId: string;
    step: {
        name: string;
        departmentId: string;
    };
};

export type Machine = {
    _id?: string;
    name: string;
    defaultMachineTime?: number;
    machineSchedule?: string[];
};

export type MachineInput = {
    _id?: string;
    name: string;
    defaultMachineTime?: number;
    machineSchedule?: string[];
};

export type DepartmentWorkflowStep = {
    _id: string;
    name: string;
    stepOrder: number;
};

export type Department = {
    _id?: string;
    name: string;
    workflowSteps: DepartmentWorkflowStep[];
    technicians: User[] | string[];
    productionHourLimit?: number;
    capacity?: number;
};

export type LinkedOrganization = {
    _id: string;
    name: string;
};
